import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import { ReactComponent as Tag } from '@assets/icons/tag.svg';
import { ROUTES } from 'constant';
import Image from 'next/image';
import Link from 'next/link';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Grid,
  GridItem,
  useBoolean,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setFulfillmentPreference } from 'store/reducers/cartSlice';
import { setParentPageType } from 'store/reducers/productsSlice';

import { formatCurrency } from 'utils';

import {
  CartItemOnlineFulfillmentEnum,
  ProductRedirectionType,
  ShopCartItem,
  ShopCartType,
  UserFulfillmentMode
} from 'types';

import { FulfillmentSelector } from 'components/FulfillmentSelector';
import { Button, Loader, Text } from 'ui';

const getPath = (type: ShopCartType) =>
  type === ShopCartType.Product ? ROUTES.productsShop : '';

interface AccordionCartDetailsProps {
  isVisible?: boolean;
  items: ShopCartItem[];
  sum: number;
  showMixedFulfillmentTag: boolean;
  invoiceId: number;
}

const AccordionCartDetails = ({
  isVisible = false,
  items,
  sum,
  showMixedFulfillmentTag,
  invoiceId
}: AccordionCartDetailsProps) => {
  const {
    colors: {
      palette: {
        shop: {
          cart: { accordion: styles }
        }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { isPreferencesMenuEnabled } = useAppSelector((state) => state.cart);
  const [loading, setLoading] = useBoolean(true);

  const handleChangeMode =
    (item: ShopCartItem) => (mode: UserFulfillmentMode) => {
      if (item?.online_fulfillment !== undefined) {
        dispatch(
          setFulfillmentPreference({
            cart_item_id: item.cart_item_id,
            original_fulfillment: item.online_fulfillment,
            user_selected: mode,
            invoice_id: invoiceId
          })
        );
      }
    };

  return (
    <Box
      display={isVisible ? 'block' : 'none'}
      bg={styles.bg}
      borderRadius='6px'
      w='full'
      overflow='hidden'
    >
      <Accordion allowToggle>
        <AccordionItem border='none'>
          {({ isExpanded }) => (
            <>
              <Flex
                as={AccordionButton}
                px={{ base: '16px', md: '30px' }}
                py='16px'
                w='full'
                justifyContent='space-between'
              >
                <Text
                  color={styles.sumColor}
                  fontSize={{ base: '16px', md: '18px' }}
                >
                  {`ORDER DETAILS: ${formatCurrency(sum)}`}
                </Text>
                <ChevronDown
                  width={16}
                  height={18}
                  style={{
                    transition: 'transform .2s',
                    transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0)'
                  }}
                />
              </Flex>

              <AccordionPanel
                bg={styles.itemBg}
                borderBottomRadius='8px'
                p={{ base: '16px', md: '30px' }}
              >
                <VStack gap='25px'>
                  {items.map((item) => {
                    const isMixedDeliveryAvailable =
                      item.online_fulfillment ===
                      CartItemOnlineFulfillmentEnum.ShippingAndPickup;
                    return (
                      <Grid
                        key={item.id}
                        templateColumns='repeat(12, 1fr)'
                        columnGap='25px'
                        rowGap={{ base: '5px', md: 0 }}
                        w='full'
                      >
                        <GridItem
                          colSpan={1}
                          alignContent={{ base: 'flex-start', md: 'center' }}
                          rowSpan={{ base: 2, md: 1 }}
                        >
                          {item?.img && !item?.img.includes('tag.png') ? (
                            <Flex
                              alignItems='center'
                              justifyContent='center'
                              bg='white'
                              borderRadius='6px'
                              overflow='hidden'
                              height='60px'
                              width='60px'
                              position='relative'
                            >
                              {loading && <Loader absolute />}
                              <Image
                                src={item?.img}
                                quality={100}
                                sizes='(max-width: 450px) 100px, 200px'
                                height={60}
                                width={60}
                                style={{
                                  objectFit: 'contain',
                                  aspectRatio: '1/1'
                                }}
                                alt={`Cart Item Image ${item?.name}`}
                                onLoad={setLoading.off}
                              />
                            </Flex>
                          ) : (
                            <Flex
                              alignItems='center'
                              justifyContent='center'
                              my='auto'
                              position='relative'
                              maxH='60px'
                              maxW='60px'
                              h='60px'
                              w='60px'
                              bg={styles.emptyImageBg}
                              borderRadius='6px'
                              overflow='hidden'
                              sx={{
                                '& > svg': {
                                  width: { base: '30px', sm: '40px' },
                                  height: { base: '30px', sm: '40px' }
                                }
                              }}
                            >
                              <Tag fill={styles.defaultIconColor} />
                            </Flex>
                          )}
                        </GridItem>
                        <GridItem
                          colSpan={{ base: 11, md: 5 }}
                          alignContent={{ base: 'flex-start', md: 'center' }}
                        >
                          <VStack gap='4px' w='full' alignItems='flex-start'>
                            <Button
                              h='auto'
                              textTransform='initial'
                              minH={0}
                              letterSpacing='initial'
                              variant='text'
                              p={0}
                              display='block'
                              _hover={{
                                opacity: getPath(item.item_type) ? 0.5 : 1
                              }}
                              as={Link}
                              href={`${getPath(item.item_type)}/${item.id}`}
                              onClick={(e) => {
                                dispatch(
                                  setParentPageType(
                                    ProductRedirectionType.ANY_PAGE
                                  )
                                );
                                if (!getPath(item.item_type)) {
                                  e.preventDefault();
                                }
                              }}
                              isDisabled={!getPath(item.item_type)}
                              _disabled={{
                                color: 'initial'
                              }}
                              cursor={
                                getPath(item.item_type) ? 'pointer' : 'default'
                              }
                            >
                              <Text
                                fontSize={['20px', '23px', '25px']}
                                fontWeight='regular'
                                color={styles.textColor}
                              >
                                {item.name}
                              </Text>
                            </Button>
                            {item.size && (
                              <Text
                                fontSize={['12px', '14px']}
                                color={styles.textColor}
                              >
                                {item.size}
                              </Text>
                            )}
                          </VStack>

                          {showMixedFulfillmentTag && (
                            <Flex mt='10px'>
                              <FulfillmentSelector
                                cartItemId={item.cart_item_id}
                                defaultMode={
                                  item.online_fulfillment ||
                                  CartItemOnlineFulfillmentEnum.ShippingAndPickup
                                }
                                isDisplayMenu={isMixedDeliveryAvailable}
                                onModeChange={handleChangeMode(item)}
                                isReadOnly={!isPreferencesMenuEnabled}
                              />
                            </Flex>
                          )}
                        </GridItem>

                        <GridItem
                          colStart={{ base: 2, md: 0 }}
                          colSpan={{ base: 5, md: 3 }}
                          alignContent='center'
                          textAlign={{ base: 'left', md: 'right' }}
                        >
                          <Text
                            fontSize={['18px', '23px', '25px']}
                            color={styles.qtyColor}
                            alignContent='center'
                            textAlign={{ base: 'left', md: 'right' }}
                          >
                            {`QTY: ${item.qty}`}
                          </Text>
                        </GridItem>

                        <GridItem
                          colSpan={{ base: 6, md: 3 }}
                          alignContent='center'
                        >
                          <Flex
                            w='full'
                            justifyContent='flex-end'
                            gap={{ base: 2, md: 4 }}
                            alignItems='center'
                          >
                            {/* TODO: add discount after client approve design */}
                            {/* <Text
                            fontSize={['14px',   '20px']}
                            color={styles.discountPriceColor}
                            textAlign='end'
                          >
                            -{formatCurrency(item.amount_total)}
                          </Text> */}
                            <Text
                              fontSize={['18px', '23px', '25px']}
                              color={styles.amount}
                              textAlign='end'
                            >
                              {formatCurrency(item.amount_total)}
                            </Text>
                          </Flex>
                        </GridItem>
                      </Grid>
                    );
                  })}
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default AccordionCartDetails;
